/**
 * ThinSeatsLine — "Seats are thin" on the focus AA flight, from the latest snapshot only.
 * Register SF2. Shown only when a read exists and shows it. Owner only: a share viewer's
 * request gets a 404 and this renders nothing.
 */
import { useQuery } from "@tanstack/react-query";
import { tripsApi } from "@/lib/api";
import type { ThinSeats } from "@/components/seats/seatTypes";

export function thinFor(thin: ThinSeats[] | undefined, flightNumber: string | null | undefined, date: string | null | undefined): ThinSeats | null {
  const digits = String(flightNumber ?? "").replace(/^\s*[A-Z][A-Z0-9]?\s*(?=\d)/i, "").replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  if (!digits || !date) return null;
  return thin?.find((t) => t.flightNumber === digits && t.date === date) ?? null;
}

const shortDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function ThinSeatsNote({ thin }: { thin: ThinSeats }) {
  return (
    <div className="mx-2 mb-1 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-950 dark:bg-amber-900/20 dark:text-amber-100">
      <p>
        <span className="font-semibold">Seats are thin:</span> {thin.text}
      </p>
      <p className="mt-0.5 opacity-80">Read {shortDay(thin.readAt)}. AA shows this as view only before you book.</p>
    </div>
  );
}

export default function ThinSeatsLine({ tripId, flightNumber, date }: { tripId: string; flightNumber: string | null | undefined; date: string | null | undefined }) {
  const { data } = useQuery({
    queryKey: ["tripSeats", tripId],
    queryFn: () => tripsApi.seats(tripId),
    retry: false,
  });
  const thin = thinFor(data?.thin, flightNumber, date);
  return thin ? <ThinSeatsNote thin={thin} /> : null;
}
